import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Card,
  Icon,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { Formik } from "formik";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { get, isEmpty } from "lodash";
import { updatePlace } from "store/slice/places/placeSlice";
import CircularIndeterminate from "components/MDLoading";
import MDSnackbar from "components/MDSnackbar";
import { getPlaces } from "store/slice/places/placeSlice";
import { setSelectedPlace } from "store/slice/places/placeSlice";
import { triggerSetupSnackbar } from "store/slice/globalControl/globalControlSlice";

const PaymentGatewayCard = (props) => {
  const { userRole } = props;
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const formikRef = useRef(null);
  const [paymentEdit, setPaymentEdit] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Only emit guide events while guide is actively running
  const isGuideActive = useSelector((state) => state.ui?.guide?.active ?? false);
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });
  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  let placeId =
    selectedPlace?._id || new URLSearchParams(location?.search).get("placeId");

  useEffect(() => {
    if (!isEmpty(selectedPlace)) {
      formikRef?.current?.setFieldValue(
        "placeId",
        get(selectedPlace, "_id", null),
      );
      formikRef?.current?.setFieldValue(
        "paymentGatewayFeePayBy",
        get(selectedPlace, "paymentGatewayFeePayBy", "customer"),
      );
    }
  }, [selectedPlace]);

const handlePricingTierSuccess = () => {
    // Fire guide event — GuideOverlay checks transit/active state, so it's safe to always dispatch
    window.dispatchEvent(
      new CustomEvent("GUIDE_STEP_COMPLETE", {
        detail: { step: "PRICING_TIER" },
      }),
    );
  };

  const handelPaymentFeeUpdate = (rest) => {
    const type = "UPDATE";
    setIsLoading(true);
    dispatch(updatePlace({ placeId, type, ...rest }))
      .unwrap()
      .then(async (res) => {
        const success = res?.success;
        setIsLoading(false);
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
        if (success) {
          await dispatch(getPlaces());
          await dispatch(setSelectedPlace(res?.updatedPlaceData));
          handlePricingTierSuccess();
          setPaymentEdit(false);
          // ✅ Use fresh data from API response (not stale selectedPlace)
          dispatch(triggerSetupSnackbar(res?.updatedPlaceData?.setup));
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message,
          icon: "warning",
          show: true,
        });
      });
  };

  return (
    <>
      <Card id="pricing-tier-card" sx={{ boxShadow: 3 }}>
        <MDBox p={2}>
          <Formik
            innerRef={formikRef}
            initialValues={{
              paymentGatewayFeePayBy: "",
            }}
            onSubmit={(value, action) => {
              const currentSetup = selectedPlace?.setup || {};
              const currentSteps = currentSetup?.steps || {};
              const payload = {
                ...value,
                setup: {
                  ...currentSetup,
                  steps: {
                    ...currentSteps,
                    pricingTierSettings: true,
                  },
                },
              };
              handelPaymentFeeUpdate(payload);
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <Grid
                  container
                  spacing={0}
                  minHeight={paymentEdit ? null : 100}
                >
                  <Grid item xs={12} container alignItems="center">
                    <MDTypography
                      color="dark"
                      display="block"
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ flex: 1 }}
                    >
                      Payment Gateway Fee Paid By?
                    </MDTypography>

                    {(userRole === 100 || userRole === 90) && (
                      <IconButton
                        onClick={() => {
                          setPaymentEdit(true);
                        }}
                        color="secondary"
                      >
                        <Icon fontSize="small" title="Edit">
                          editable
                        </Icon>
                      </IconButton>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <RadioGroup
                      name="paymentGatewayFeePayBy"
                      sx={{ flexDirection: "row" }}
                      value={props.values.paymentGatewayFeePayBy || ""}
                      onChange={(e) =>
                        props.setFieldValue(
                          "paymentGatewayFeePayBy",
                          e.target.value,
                        )
                      }
                    >
                      <FormControlLabel
                        value="customer"
                        control={<Radio />}
                        label="Customer"
                        disabled={!paymentEdit}
                      />
                      <FormControlLabel
                        value="client"
                        control={<Radio />}
                        label="Client"
                        disabled={!paymentEdit}
                      />
                      {!selectedPlace.isDirectChargeLocation &&
                        userRole === 100 && (
                          <FormControlLabel
                            value="dock"
                            control={<Radio />}
                            label="Dock"
                            disabled={!paymentEdit}
                          />
                        )}
                    </RadioGroup>
                  </Grid>
                  <Grid
                    xs={12}
                    item
                    gap={2}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    {paymentEdit && (
                      <>
                        <MDButton
                          color={sidenavColor}
                          variant="contained"
                          onClick={() => {
                            setPaymentEdit(false);
                          }}
                        >
                          Cancel
                        </MDButton>
                        <MDButton
                          color={sidenavColor}
                          variant="contained"
                          type="submit"
                        >
                          Save
                        </MDButton>
                      </>
                    )}
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </MDBox>
      </Card>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Updating Pricing.. "
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
    </>
  );
};

export default PaymentGatewayCard;
